import React from 'react';
import clsx from 'clsx';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
  requiredIndicator?: boolean;
  showCount?: boolean;
  currentCount?: number;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, helperText, requiredIndicator, showCount, currentCount, className = '', id, rows = 3, ...props }, ref) => {
    const textareaId = id || (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined);

    return (
      <div className="w-full flex flex-col gap-1">
        {label && (
          <div className="flex items-center justify-between">
            <label htmlFor={textareaId} className="text-xs sm:text-sm font-semibold text-[#1E293B] flex items-center gap-1">
              {label}
              {(requiredIndicator || props.required) && <span className="text-rose-500">*</span>}
            </label>
            {showCount && props.maxLength && (
              <span className={clsx(
                "text-xs font-medium tracking-tight",
                (currentCount || 0) >= props.maxLength ? "text-rose-600 font-semibold" : "text-slate-400"
              )}>
                {currentCount || 0}/{props.maxLength} max
              </span>
            )}
          </div>
        )}
        <textarea
          id={textareaId}
          ref={ref}
          rows={rows}
          className={clsx(
            'w-full px-3 py-2 text-xs sm:text-sm text-[#1E293B] bg-white border border-[#CBD5E1] rounded-lg transition-all placeholder:text-slate-400 shadow-2xs',
            'hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-[#6366F1]/20 focus:border-[#6366F1]',
            'disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed',
            error ? 'border-rose-400 focus:ring-rose-400/20 focus:border-rose-500 bg-rose-50/20' : '',
            className
          )}
          {...props}
        />
        {error ? (
          <p className="text-[11px] text-red-600 font-medium mt-0.5">{error}</p>
        ) : helperText ? (
          <p className="text-[11px] text-slate-500 mt-0.5">{helperText}</p>
        ) : null}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';
export default Textarea;
